import { useEffect, useState, useCallback, useMemo } from "react";
import "../../css/lien.css";
import { FaLink, FaArrowsAltH, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { updateSiteStatus } from "../../siteSlice";

import CardCustom from "../CardCustom";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import {
  getAllAnnonceByUser,
  getAllAnnoncesUser,
} from "../../services/Annonce_service";
import {
  getAllClientByUser,
  getAllClientByUsers,
} from "../../services/Login_service";
import { getUserDetails } from "../../services/User_service";
import { getAvoirs } from "../../services/Avoir_service";
import { CreditNoteType } from "../../static/types/creditNoteType";

import {
  faFileAlt,
  faClipboardList,
  faEnvelope,
  faStar,
  faGift,
} from "@fortawesome/free-solid-svg-icons";
import useAppContext from "../../hooks/useAppContext";
import { AppContextType } from "../../context/AppContext";
import { trackMenuClick } from "../../services/menuClick_service";

interface Stats {
  invoices: number;
  clients: number;
  ads: number;
  email_per_month: number;
}

function Header() {
  const authUser = useSelector((state: RootState) => state.user.user);
  const siteEnabled = useSelector((state: RootState) => state.site.siteEnabled);
  const dispatch = useDispatch<AppDispatch>();

  const [stats, setStats] = useState<Stats>({
    invoices: 0,
    clients: 0,
    ads: 0,
    email_per_month: 0,
  });
  
  const { userPoints, setUserPoints } = useAppContext() as AppContextType;
  const [userAvoirs, setUserAvoirs] = useState<number>(0);
  const navigate = useNavigate();
  const [role, setRole] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [swiperLoaded, setSwiperLoaded] = useState<boolean>(false);

  // --- ALIGNEMENT : calcul des avoirs conforme à CreditNote ---
  const calculateUserAvoirsTotal = useCallback((creditNotes: CreditNoteType[]): number => {
    const userId = localStorage.getItem("userId");
    if (!userId || !Array.isArray(creditNotes)) return 0;

    const filtered = creditNotes.filter(cn =>
      cn.annonce?.User?.id?.toString() === userId &&
      cn.statut === 'pending'
    );

    const total = filtered.reduce((sum, avoir) => {
      return sum + (Number(avoir.montant_avoir) || 0);
    }, 0);

    return Math.round(total * 100) / 100;
  }, []);

  const fetchUserData = useCallback(async (userRole: string) => {
    if (!userRole) {
      toast.error("Rôle utilisateur non défini");
      setLoading(false);
      return;
    }

    try {
      let annonces: any[] = [];
      if (userRole === "CLIENT") {
        annonces = (await getAllAnnonceByUser()) || [];
      } else if (userRole === "ADMIN") {
        annonces = (await getAllAnnoncesUser()) || [];
      } else {
        toast.error("Rôle non reconnu");
        setLoading(false);
        return;
      }

      if (Array.isArray(annonces)) {
        setStats(prev => ({
          ...prev,
          ads: annonces.length,
          invoices: annonces.length,
        }));
      }

      let clients: any[] = [];
      const userId = localStorage.getItem("userId");
      if (userId) {
        if (userRole === "ADMIN") {
          clients = await getAllClientByUsers(parseInt(userId));
        } else if (userRole === "CLIENT") {
          clients = await getAllClientByUser(parseInt(userId));
        }

        setStats(prev => ({
          ...prev,
          clients: clients.length,
        }));
      }

      if (userRole === "CLIENT") {
        try {
          const avoirsData: CreditNoteType[] = await getAvoirs();
          if (avoirsData && Array.isArray(avoirsData)) {
            const totalAvoirs = calculateUserAvoirsTotal(avoirsData);
            setUserAvoirs(totalAvoirs);
          } else {
            setUserAvoirs(0);
          }
        } catch (error) {
          console.error("Erreur lors de la récupération des avoirs:", error);
          setUserAvoirs(0);
        }
      }

      const user = await getUserDetails();
      if (user && user.points_balance !== undefined) {
        setUserPoints(user.points_balance);
      }

    } catch (error) {
      console.error("Erreur lors de la récupération des données:", error);
      toast.error("Erreur lors de la récupération des données");
      localStorage.removeItem("userId");
      navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [calculateUserAvoirsTotal, navigate, setUserPoints]);

  useEffect(() => {
    const userRole = localStorage.getItem("role");
    if (userRole) {
      setRole(userRole);
      fetchUserData(userRole);
    }
  }, [fetchUserData]);

  useEffect(() => {
    if (authUser) {
      setStats(prev => ({
        ...prev,
        email_per_month: authUser.email_sent_number || 0,
      }));
    }
  }, [authUser]);

  // Handler pour le toggle site avec tracking
  const handleToggleSiteWithTracking = useCallback(() => {
    trackMenuClick(
      'admin-toggle-site', 
      siteEnabled ? 'Désactiver le site' : 'Activer le site'
    );
    dispatch(updateSiteStatus(!siteEnabled));
    toast.info(`Site ${!siteEnabled ? 'activé' : 'désactivé'}`);
  }, [dispatch, siteEnabled]);

  // Handler pour le clic sur les cartes
  const handleCardClick = (card: any) => {
    // Tracker le clic
    trackMenuClick(card.menuKey, card.menuLabel);
    
    // Exécuter l'onClick personnalisé s'il existe
    if (card.onClick) {
      card.onClick();
    }
    
    // Naviguer si targetUrl existe
    if (card.targetUrl) {
      navigate(card.targetUrl);
    }
  };

  // Handler pour le clic sur le logo
  const handleLogoClick = () => {
    trackMenuClick('header-logo-link', 'Lien vers site principal');
  };

  // Cartes pour les clients avec tracking
  const clientCards = useMemo(() => [
    {
      title: "Mes Factures",
      value: stats.invoices,
      icon: faFileAlt,
      color: "#4BB0C0",
      targetUrl: "/home/gestion-factures",
      menuKey: "header-mes-factures",
      menuLabel: "Header - Mes Factures",
    },
    {
      title: "Mes Annonces",
      value: stats.ads,
      icon: faClipboardList,
      color: "#4BB0C0",
      targetUrl: "/home/gestion-annonces",
      menuKey: "header-mes-annonces",
      menuLabel: "Header - Mes Annonces",
    },
    {
      title: "Emails envoyés",
      value: stats.email_per_month,
      icon: faEnvelope,
      color: "#4BB0C0",
      targetUrl: "/home/gestion-emails?tab=boite",
      menuKey: "header-emails-envoyes",
      menuLabel: "Header - Emails envoyés",
    },
    {
      title: "Mes avoirs",
      value: `${userAvoirs.toFixed(2)} €`,
      icon: faGift,
      color: "#ff8944ff",
      targetUrl: "/home/gestion-annonces?tab=avoirs",
      menuKey: "header-mes-avoirs",
      menuLabel: "Header - Mes avoirs",
    },
    {
      title: "Mes points",
      value: userPoints,
      icon: faStar,
      color: "#ff8944ff",
      targetUrl: undefined,
      menuKey: "header-mes-points",
      menuLabel: "Header - Mes points",
      onClick: () => {
        toast.info("Vos points fidélité");
      }
    },
  ], [stats, userAvoirs, userPoints]);

  // Cartes pour les admins avec tracking
  const adminCards = useMemo(() => [
    {
      title: "Mes Factures",
      value: stats.invoices,
      icon: faFileAlt,
      color: "#4BB0C0",
      targetUrl: "/home/gestion-factures",
      menuKey: "header-admin-factures",
      menuLabel: "Header Admin - Mes Factures",
    },
    {
      title: "Mes Annonces",
      value: stats.ads,
      icon: faClipboardList,
      color: "#4BB0C0",
      targetUrl: "/home/gestion-annonces",
      menuKey: "header-admin-annonces",
      menuLabel: "Header Admin - Mes Annonces",
    },
  ], [stats]);

  return (
    <div className="my-3">
      {/* Logo et lien avec tracking */}
      <div className="none1">
        <h2>
          <a
            href="https://annonce-legale-officielle.fr/"
            target="_blank"
            rel="noopener noreferrer"
            className="navLink"
            onClick={handleLogoClick}
          >
            Annonce Légale Officielle
            <FaLink className='customIcon' />
          </a>
        </h2>
      </div>

      {/* Bouton pour activer/désactiver le site avec tracking */}
      {authUser?.role === "ADMIN" && (
        <div className="text-center my-3">
          <button
            onClick={handleToggleSiteWithTracking}
            className="btn btn-lg d-flex align-items-center justify-content-center mx-auto"
            style={{
              padding: '12px 24px',
              backgroundColor: siteEnabled ? '#4bb0c0' : '#f5880bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              minWidth: '220px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
            }}
          >
            {siteEnabled ? (
              <>
                <FaToggleOn className="me-2" style={{ fontSize: '20px' }} />
                Désactiver le site
              </>
            ) : (
              <>
                <FaToggleOff className="me-2" style={{ fontSize: '20px' }} />
                Activer le site
              </>
            )}
          </button>
          <p className="text-muted mt-2 mb-0" style={{ fontSize: '14px' }}>
            {siteEnabled 
              ? 'Le site est actuellement accessible aux utilisateurs' 
              : 'Le site est actuellement en maintenance'
            }
          </p>
        </div>
      )}

      {/* Carrousel Swiper pour mobile uniquement avec tracking */}
      <div className="d-block d-md-none">
        <div className="text-center mb-2" style={{ fontSize: 14, color: '#666' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FaArrowsAltH style={{ color: '#4BB0C0', fontSize: 16 }} />
            Faites glisser pour voir plus de cartes
          </span>
        </div>
        <div className="header-swiper-container" style={{ maxWidth: 320, margin: '0 auto', padding: '0 10px' }}>
          <Swiper
            spaceBetween={12}
            slidesPerView={1.1}
            centeredSlides={false}
            grabCursor={true}
            style={{ padding: '8px 0 24px' }}
            onInit={() => setSwiperLoaded(true)}
          >
            {(authUser?.role === "ADMIN" ? adminCards : clientCards).map((card, index) => (
              <SwiperSlide key={index}>
                <div 
                  style={{ 
                    height: '100%',
                    opacity: swiperLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleCardClick(card)}
                >
                  <CardCustom
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    color={card.color}
                    // Ne pas passer targetUrl pour éviter double navigation
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>

      {/* Grille classique pour desktop/tablette avec tracking */}
      <div className="d-none d-md-block">
        {authUser?.role !== "ADMIN" ? (
          // Pour les clients : 5 cartes responsive
          <div className="row justify-content-center g-3">
            {clientCards.map((card, index) => (
              <div 
                key={index}
                className="col-xxl-2 col-xl-2 col-lg-3 col-md-4 col-sm-6 my-2 d-flex justify-content-center"
                onClick={() => handleCardClick(card)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ 
                  width: "100%", 
                  maxWidth: "14rem",
                  minWidth: "12rem"
                }}>
                  <CardCustom
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    color={card.color}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="row justify-content-center g-4">
            {adminCards.map((card, index) => (
              <div 
                key={index}
                className="col-xl-3 col-lg-4 col-md-6 my-2 d-flex justify-content-center"
                onClick={() => handleCardClick(card)}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ 
                  width: "100%", 
                  maxWidth: "16rem",
                  minWidth: "14rem"
                }}>
                  <CardCustom
                    title={card.title}
                    value={card.value}
                    icon={card.icon}
                    color={card.color}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Header;